import React, { useState, useEffect } from 'react';
import { cityService } from '../services/api';

// ════════════════════════════════════════════════════════════════
// Create City Modal
// ════════════════════════════════════════════════════════════════
export const CreateCityModal = ({ onClose, onSuccess, onError }) => {
  const [form, setForm] = useState({
    name: '',
    countryCode: '',
    timezone: '',
    utcOffset: '',
    bboxSouth: '',
    bboxWest: '',
    bboxNorth: '',
    bboxEast: '',
    pollIntervalMinutes: '12',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await cityService.create({
        name: form.name,
        countryCode: form.countryCode,
        timezone: form.timezone,
        utcOffset: parseFloat(form.utcOffset),
        bbox: {
          south: parseFloat(form.bboxSouth),
          west: parseFloat(form.bboxWest),
          north: parseFloat(form.bboxNorth),
          east: parseFloat(form.bboxEast),
        },
        pollIntervalMinutes: parseInt(form.pollIntervalMinutes),
      });
      onSuccess(result.message || `City "${form.name}" created`);
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to create city');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New City</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>City Name <span style={{ color: '#c62828' }}>*</span></label>
              <input type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Mumbai" required minLength={2} maxLength={100} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Country Code <span style={{ color: '#c62828' }}>*</span></label>
                <input type="text" value={form.countryCode}
                  onChange={(e) => handleChange('countryCode', e.target.value.toUpperCase())}
                  placeholder="IN" required maxLength={2} minLength={2} />
                <span className="form-hint">ISO 3166-1 alpha-2</span>
              </div>
              <div className="form-group">
                <label>UTC Offset <span style={{ color: '#c62828' }}>*</span></label>
                <input type="number" step="0.5" value={form.utcOffset}
                  onChange={(e) => handleChange('utcOffset', e.target.value)}
                  placeholder="5.5" required />
              </div>
            </div>

            <div className="form-group">
              <label>Timezone <span style={{ color: '#c62828' }}>*</span></label>
              <input type="text" value={form.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                placeholder="Asia/Kolkata" required />
              <span className="form-hint">IANA timezone identifier</span>
            </div>

            <div className="form-section">
              <label>Bounding Box <span style={{ color: '#c62828' }}>*</span></label>
              <span className="form-hint" style={{ marginBottom: 8, display: 'block' }}>Geographic bounds for hex grid generation</span>
              <div className="form-row">
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>South (min lat)</label>
                  <input type="number" step="0.001" value={form.bboxSouth}
                    onChange={(e) => handleChange('bboxSouth', e.target.value)}
                    placeholder="18.88" required />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>West (min lng)</label>
                  <input type="number" step="0.001" value={form.bboxWest}
                    onChange={(e) => handleChange('bboxWest', e.target.value)}
                    placeholder="72.77" required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>North (max lat)</label>
                  <input type="number" step="0.001" value={form.bboxNorth}
                    onChange={(e) => handleChange('bboxNorth', e.target.value)}
                    placeholder="19.27" required />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>East (max lng)</label>
                  <input type="number" step="0.001" value={form.bboxEast}
                    onChange={(e) => handleChange('bboxEast', e.target.value)}
                    placeholder="72.98" required />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Poll Interval</label>
              <select value={form.pollIntervalMinutes} onChange={(e) => handleChange('pollIntervalMinutes', e.target.value)}>
                <option value="1">1 min (~$864/mo per 5 sentinels)</option>
                <option value="3">3 min (~$288/mo)</option>
                <option value="6">6 min (~$124/mo)</option>
                <option value="12">12 min (~$66/mo) — recommended</option>
                <option value="30">30 min (~$28/mo)</option>
                <option value="60">60 min (~$15/mo)</option>
              </select>
              <span className="form-hint">How often sentinel routes are polled for traffic data</span>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create City'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Edit City Modal
// ════════════════════════════════════════════════════════════════
export const EditCityModal = ({ city, onClose, onSuccess, onError }) => {
  const bbox = city.bbox || { north: 0, south: 0, east: 0, west: 0 };
  const [form, setForm] = useState({
    name: city.name || '',
    countryCode: city.countryCode || '',
    timezone: city.timezone || '',
    utcOffset: city.utcOffset?.toString() || '',
    bboxSouth: bbox.south?.toString() || '',
    bboxWest: bbox.west?.toString() || '',
    bboxNorth: bbox.north?.toString() || '',
    bboxEast: bbox.east?.toString() || '',
    pollIntervalMinutes: city.pollIntervalMinutes?.toString() || '12',
    status: city.status || 'active',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await cityService.update(city.id, {
        name: form.name,
        countryCode: form.countryCode,
        timezone: form.timezone,
        utcOffset: parseFloat(form.utcOffset),
        bbox: {
          south: parseFloat(form.bboxSouth),
          west: parseFloat(form.bboxWest),
          north: parseFloat(form.bboxNorth),
          east: parseFloat(form.bboxEast),
        },
        pollIntervalMinutes: parseInt(form.pollIntervalMinutes),
        status: form.status,
      });
      onSuccess(`City "${form.name}" updated`);
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to update city');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit City — {city.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>City Name <span style={{ color: '#c62828' }}>*</span></label>
              <input type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)} required minLength={2} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Country Code <span style={{ color: '#c62828' }}>*</span></label>
                <input type="text" value={form.countryCode}
                  onChange={(e) => handleChange('countryCode', e.target.value.toUpperCase())}
                  required maxLength={2} minLength={2} />
              </div>
              <div className="form-group">
                <label>UTC Offset <span style={{ color: '#c62828' }}>*</span></label>
                <input type="number" step="0.5" value={form.utcOffset}
                  onChange={(e) => handleChange('utcOffset', e.target.value)} required />
              </div>
            </div>

            <div className="form-group">
              <label>Timezone <span style={{ color: '#c62828' }}>*</span></label>
              <input type="text" value={form.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)} required />
            </div>

            <div className="form-section">
              <label>Bounding Box</label>
              <div className="form-row">
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>South</label>
                  <input type="number" step="0.001" value={form.bboxSouth}
                    onChange={(e) => handleChange('bboxSouth', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>West</label>
                  <input type="number" step="0.001" value={form.bboxWest}
                    onChange={(e) => handleChange('bboxWest', e.target.value)} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>North</label>
                  <input type="number" step="0.001" value={form.bboxNorth}
                    onChange={(e) => handleChange('bboxNorth', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>East</label>
                  <input type="number" step="0.001" value={form.bboxEast}
                    onChange={(e) => handleChange('bboxEast', e.target.value)} required />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Poll Interval</label>
                <select value={form.pollIntervalMinutes} onChange={(e) => handleChange('pollIntervalMinutes', e.target.value)}>
                  <option value="1">1 min</option>
                  <option value="3">3 min</option>
                  <option value="6">6 min</option>
                  <option value="12">12 min</option>
                  <option value="30">30 min</option>
                  <option value="60">60 min</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                  <option value="draft">Draft</option>
                  <option value="seeding">Seeding</option>
                  <option value="enriching">Enriching</option>
                  <option value="calibrating">Calibrating</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Add Sentinel Route Modal
// ════════════════════════════════════════════════════════════════
export const AddRouteModal = ({ cityId, onClose, onSuccess, onError }) => {
  const [form, setForm] = useState({
    routeName: '',
    roadClassification: 'arterial',
    originLat: '',
    originLng: '',
    destLat: '',
    destLng: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await cityService.addRoute(cityId, {
        routeName: form.routeName,
        roadClassification: form.roadClassification,
        originLat: parseFloat(form.originLat),
        originLng: parseFloat(form.originLng),
        destLat: parseFloat(form.destLat),
        destLng: parseFloat(form.destLng),
      });
      onSuccess(`Route "${form.routeName}" added as candidate`);
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to add route');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Sentinel Route</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Route Name <span style={{ color: '#c62828' }}>*</span></label>
              <input type="text" value={form.routeName}
                onChange={(e) => handleChange('routeName', e.target.value)}
                placeholder="e.g. MARINE_DR, WEH, JVLR" required />
              <span className="form-hint">Unique identifier for this route within the city</span>
            </div>

            <div className="form-group">
              <label>Road Classification <span style={{ color: '#c62828' }}>*</span></label>
              <select value={form.roadClassification}
                onChange={(e) => handleChange('roadClassification', e.target.value)}>
                <option value="highway">Highway</option>
                <option value="arterial">Arterial</option>
                <option value="local">Local</option>
              </select>
            </div>

            <div className="form-section">
              <label>Origin Coordinates</label>
              <div className="form-row">
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>Latitude</label>
                  <input type="number" step="0.0001" value={form.originLat}
                    onChange={(e) => handleChange('originLat', e.target.value)}
                    placeholder="19.0368" required />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>Longitude</label>
                  <input type="number" step="0.0001" value={form.originLng}
                    onChange={(e) => handleChange('originLng', e.target.value)}
                    placeholder="72.8145" required />
                </div>
              </div>
            </div>

            <div className="form-section">
              <label>Destination Coordinates</label>
              <div className="form-row">
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>Latitude</label>
                  <input type="number" step="0.0001" value={form.destLat}
                    onChange={(e) => handleChange('destLat', e.target.value)}
                    placeholder="18.9399" required />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.75rem' }}>Longitude</label>
                  <input type="number" step="0.0001" value={form.destLng}
                    onChange={(e) => handleChange('destLng', e.target.value)}
                    placeholder="72.8234" required />
                </div>
              </div>
            </div>

            <div className="form-help">
              Routes are added as candidates. Run calibration to automatically select the best sentinel routes.
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Adding...' : 'Add Route'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Pipeline Confirmation Modal — verbose, data-driven
// ════════════════════════════════════════════════════════════════
export const PipelineConfirmModal = ({ action, cityId, onClose, onConfirm, loading }) => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, loading]);

  useEffect(() => {
    if (cityId) {
      cityService.getPipelineStats(cityId)
        .then(res => setStats(res.data))
        .catch(() => setStats(null))
        .finally(() => setStatsLoading(false));
    }
  }, [cityId]);

  const fmtNum = (n) => n == null ? '0' : n.toLocaleString();
  const needsConfirmType = action.action === 'seed';
  const canConfirm = needsConfirmType ? confirmText === 'confirm' : true;

  const renderSeedDetails = () => {
    if (!stats) return null;
    const { city, estimates } = stats;
    const hasExisting = city.hexCountTotal > 0;
    return (
      <>
        <div style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#e65100' }}>⚠ Critical Operation</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#bf360c' }}>
            This generates H3 hex cells at resolutions 8, 9, and 10 for the entire city bounding box.
            {hasExisting ? ' Existing hexes will NOT be duplicated (upsert).' : ' This is the first seed for this city.'}
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Bounding Box</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>N {city.bbox.north}° · S {city.bbox.south}° · E {city.bbox.east}° · W {city.bbox.west}°</td>
            </tr>
            {hasExisting && (
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '6px 0', color: '#666' }}>Existing Hexes in DB</td>
                <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>
                  {fmtNum(city.hexCountTotal)} (R8: {fmtNum(city.hexCountRes8)}, R9: {fmtNum(city.hexCountRes9)}, R10: {fmtNum(city.hexCountRes10)})
                </td>
              </tr>
            )}
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Estimated Hexes to Generate</td>
              <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>
                ~{fmtNum(estimates.seedHexes.total)} (R8: ~{fmtNum(estimates.seedHexes.res8)}, R9: ~{fmtNum(estimates.seedHexes.res9)}, R10: ~{fmtNum(estimates.seedHexes.res10)})
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px 0', color: '#666' }}>Cost Impact</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>No API cost (CPU-only H3 computation). Lambda execution: &lt;$0.01</td>
            </tr>
          </tbody>
        </table>
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: 4 }}>
            Type <strong>confirm</strong> to proceed:
          </label>
          <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
            placeholder="confirm" autoFocus
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem' }} />
        </div>
      </>
    );
  };

  const renderEnrichDetails = () => {
    if (!stats) return null;
    const { city, estimates } = stats;
    return (
      <>
        <div style={{ background: '#fff8e1', border: '1px solid #fff176', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#f57f17' }}>💰 Billable Operation</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#827717' }}>
            Calls Google Nearest Roads API for each hex cell to classify road types (highway, arterial, local). Idempotent — safe to re-run.
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Total Hexes in DB</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>{fmtNum(city.hexCountTotal)}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Already Enriched (road classified)</td>
              <td style={{ padding: '6px 0', textAlign: 'right', color: '#2e7d32' }}>{fmtNum(city.roadHexCount)}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Unenriched (will be processed)</td>
              <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>
                {fmtNum(estimates.hexesToEnrich)}
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Google API Calls</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>~{fmtNum(estimates.hexesToEnrich)} requests</td>
            </tr>
            <tr>
              <td style={{ padding: '6px 0', color: '#666' }}>Estimated Cost</td>
              <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, color: estimates.enrichCost > 5 ? '#c62828' : '#333' }}>
                ~${estimates.enrichCost.toFixed(2)} (@ $1 per 10K requests)
              </td>
            </tr>
          </tbody>
        </table>
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
          Processing time: ~{Math.ceil(estimates.hexesToEnrich / 500)} minutes (batched, 500 hex/min).
        </p>
      </>
    );
  };

  const renderCalibrateDetails = () => {
    if (!stats) return null;
    const { routes, lastCalibration, estimates } = stats;
    return (
      <>
        <div style={{ background: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#1565c0' }}>🔬 Calibration Run</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#0d47a1' }}>
            Tests all candidate routes against Google Routes API at current traffic conditions, then
            selects the top K sentinels that minimize prediction error across the hex grid.
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Candidate Routes</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>{routes.candidates} candidates, {routes.sentinels} current sentinels</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Google API Calls</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>~{routes.total} calls (1 per route)</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Estimated Cost</td>
              <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>~${estimates.calibrationCost.toFixed(2)}</td>
            </tr>
            {lastCalibration && (
              <>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px 0', color: '#666' }}>Last Calibration</td>
                  <td style={{ padding: '6px 0', textAlign: 'right' }}>{new Date(lastCalibration.date).toLocaleDateString()} — {lastCalibration.status}</td>
                </tr>
                {lastCalibration.meanErrorPp != null && (
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px 0', color: '#666' }}>Current Error</td>
                    <td style={{ padding: '6px 0', textAlign: 'right' }}>{lastCalibration.meanErrorPp.toFixed(2)} pp mean · {lastCalibration.usablePct?.toFixed(1)}% usable</td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
          Result: new sentinels selected within ~1 minute. Old sentinels are replaced.
        </p>
      </>
    );
  };

  const renderPollDetails = () => {
    if (!stats) return null;
    const { city, routes, estimates, totalReadings } = stats;
    return (
      <>
        <div style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#2e7d32' }}>📡 One-Time Traffic Poll</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#1b5e20' }}>
            Queries Google Routes API for all active sentinel routes, stores readings, then runs
            IDW extrapolation to update traffic density for the entire hex grid.
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Active Sentinels</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>{routes.sentinels} routes</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Google API Calls</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>{estimates.apiCallsPerPoll} requests (1 per sentinel)</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Cost of 1 Poll</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>${(estimates.apiCallsPerPoll * 0.005).toFixed(3)}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Existing Readings</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>{fmtNum(totalReadings)} readings</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 0', color: '#666' }}>Scheduled Polling</td>
              <td style={{ padding: '6px 0', textAlign: 'right' }}>Every {city.pollIntervalMinutes} min (cron) — ${estimates.pollCostPerDay.toFixed(2)}/day · ${estimates.pollCostPerMonth.toFixed(2)}/mo</td>
            </tr>
          </tbody>
        </table>
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
          Results appear in sentinel readings within ~30 seconds. Traffic distribution updates immediately.
        </p>
      </>
    );
  };

  const renderGlobalWarning = () => (
    <div style={{ background: '#fce4ec', border: '1px solid #f8bbd0', borderRadius: 6, padding: 10, marginBottom: 12 }}>
      <strong style={{ color: '#c62828' }}>⚠ Global Action</strong>
      <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#b71c1c' }}>
        This affects ALL cities, not just the currently viewed city. The Lambda processes every qualifying hex cell across the entire system.
      </p>
    </div>
  );

  const infoRow = (label, value) => (
    <tr style={{ borderBottom: '1px solid #eee' }}>
      <td style={{ padding: '6px 0', color: '#666' }}>{label}</td>
      <td style={{ padding: '6px 0', textAlign: 'right' }}>{value}</td>
    </tr>
  );

  const renderWeatherDetails = () => {
    if (!stats) return null;
    const { city } = stats;
    const res8 = city.hexCountRes8 || 0;
    return (
      <>
        {renderGlobalWarning()}
        <div style={{ background: '#fff8e1', border: '1px solid #fff176', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#f57f17' }}>🌤 Weather Poll</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#827717' }}>
            Polls Google Weather API for all device-active res-8 hex cells. Stores temperature, humidity, wind, precipitation, UV index, and conditions.
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}><tbody>
          {infoRow('Resolution', 'Res-8 (native)')}
          {infoRow('This City Res-8 Cells', fmtNum(res8))}
          {infoRow('Google API Calls', `~${fmtNum(res8)} requests`)}
          {infoRow('Estimated Cost', `~$${(res8 * 0.0005).toFixed(2)} (@ $0.50 per 1K requests)`)}
          {infoRow('EventBridge Schedule', 'rate(1 hour) — automatic')}
          {infoRow('Signal TTL', '2 hours')}
        </tbody></table>
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
          Results stored in hex_cell_state with signal_type='weather'. Cached in Redis for 2h.
        </p>
      </>
    );
  };

  const renderAqiDetails = () => {
    if (!stats) return null;
    const { city } = stats;
    const res8 = city.hexCountRes8 || 0;
    return (
      <>
        {renderGlobalWarning()}
        <div style={{ background: '#e8eaf6', border: '1px solid #c5cae9', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#283593' }}>💨 Air Quality Poll</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#1a237e' }}>
            Polls Google Air Quality API for all device-active res-8 cells. Stores AQI index, category, dominant pollutant, and pollutant concentrations.
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}><tbody>
          {infoRow('Resolution', 'Res-8 (native)')}
          {infoRow('This City Res-8 Cells', fmtNum(res8))}
          {infoRow('Google API Calls', `~${fmtNum(res8)} requests`)}
          {infoRow('Estimated Cost', `~$${(res8 * 0.005).toFixed(2)} (@ $5 per 1K requests)`)}
          {infoRow('EventBridge Schedule', 'rate(3 hours) — automatic')}
          {infoRow('Signal TTL', '6 hours')}
        </tbody></table>
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
          AQI data is more expensive than weather. Results stored in hex_cell_state with signal_type='aqi'.
        </p>
      </>
    );
  };

  const renderSceneDetails = () => {
    if (!stats) return null;
    const { city } = stats;
    const res9 = city.hexCountRes9 || 0;
    return (
      <>
        {renderGlobalWarning()}
        <div style={{ background: '#fce4ec', border: '1px solid #f8bbd0', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#c62828' }}>📍 Scene Context — Expensive Operation</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#b71c1c' }}>
            Calls 3 Google APIs per hex (Geocoding + Nearby Search + Place Details) for all res-9 cells. This is the most expensive signal pipeline.
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}><tbody>
          {infoRow('Resolution', 'Res-9 (native)')}
          {infoRow('This City Res-9 Cells', fmtNum(res9))}
          {infoRow('Google API Calls', `~${fmtNum(res9 * 3)} (3 APIs × ${fmtNum(res9)} cells)`)}
          {infoRow('Estimated Cost', `~$${(res9 * 0.015).toFixed(2)} (@ ~$5/1K per API × 3)`)}
          {infoRow('EventBridge Schedule', 'rate(30 days) — monthly')}
          {infoRow('Signal TTL', '60 days')}
        </tbody></table>
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
          Captures POI density, place types, neighborhood context. Only re-run if city boundaries changed.
        </p>
      </>
    );
  };

  const renderSceneRes10Details = () => {
    if (!stats) return null;
    const { city } = stats;
    const res10 = city.hexCountRes10 || 0;
    return (
      <>
        {renderGlobalWarning()}
        <div style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#e65100' }}>🔬 Scene Res-10 Expansion</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#bf360c' }}>
            Expands worthy res-9 parent cells (high POI density, commercial zones) to their res-10 children for finer-grained scene context.
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}><tbody>
          {infoRow('Resolution', 'Res-10 (expanded from res-9)')}
          {infoRow('This City Res-10 Cells', fmtNum(res10))}
          {infoRow('Processing', 'CPU only — no external API calls')}
          {infoRow('Estimated Cost', '< $0.01 (Lambda execution only)')}
          {infoRow('EventBridge Schedule', 'rate(90 days) — quarterly')}
        </tbody></table>
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
          Runs after scene context poll. Only expands parents meeting threshold criteria.
        </p>
      </>
    );
  };

  const renderLabelsDetails = () => {
    if (!stats) return null;
    const { city } = stats;
    return (
      <>
        {renderGlobalWarning()}
        <div style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <strong style={{ color: '#2e7d32' }}>🏷 Derive Semantic Labels</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#1b5e20' }}>
            Derives ~20 semantic label types (commercial_density, transit_hub, school_zone, hospital_proximity, etc.) from scene context + road enrichment data.
          </p>
        </div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}><tbody>
          {infoRow('Input', 'hex_cell_state (scene_context) + hex_cells (road data)')}
          {infoRow('Output', 'hex_semantic_labels (~20 label types per hex)')}
          {infoRow('Total Hexes', fmtNum(city.hexCountTotal))}
          {infoRow('Processing', 'CPU only — no external API calls')}
          {infoRow('Estimated Cost', '< $0.01 (Lambda execution only)')}
          {infoRow('EventBridge Schedule', 'Not scheduled — manual trigger')}
        </tbody></table>
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
          Run after scene context poll to refresh labels. Safe to re-run — uses upsert.
        </p>
      </>
    );
  };

  const renderPartitionsDetails = () => (
    <>
      {renderGlobalWarning()}
      <div style={{ background: '#f3e5f5', border: '1px solid #e1bee7', borderRadius: 6, padding: 12, marginBottom: 12 }}>
        <strong style={{ color: '#6a1b9a' }}>🗄 Partition Management</strong>
        <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#4a148c' }}>
          Creates future monthly partitions for hex_cell_state table and optionally detaches old partitions
          beyond the retention window to keep query performance optimal.
        </p>
      </div>
      <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}><tbody>
        {infoRow('Table', 'hex_cell_state (partitioned by snapshot_time)')}
        {infoRow('Action', 'Create next 3 months + detach > 6 months old')}
        {infoRow('Processing', 'DDL operations only — no data movement')}
        {infoRow('Estimated Cost', '< $0.01 (Lambda execution only)')}
        {infoRow('EventBridge Schedule', 'rate(1 day) — automatic')}
      </tbody></table>
      <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#888' }}>
        Safe operation. Partitions are created idempotently. Detach does NOT delete data.
      </p>
    </>
  );

  const renderDetails = () => {
    if (statsLoading) return <div className="loading" style={{ padding: 20 }}><div className="spinner"></div></div>;
    switch (action.action) {
      case 'seed': return renderSeedDetails();
      case 'enrich': return renderEnrichDetails();
      case 'calibrate': return renderCalibrateDetails();
      case 'poll': return renderPollDetails();
      case 'weather': return renderWeatherDetails();
      case 'aqi': return renderAqiDetails();
      case 'scene': return renderSceneDetails();
      case 'scene-res10': return renderSceneRes10Details();
      case 'derive-labels': return renderLabelsDetails();
      case 'manage-partitions': return renderPartitionsDetails();
      default: return <p>{action.desc}</p>;
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>{action.label}</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}>×</button>
        </div>
        <div className="modal-body">
          {renderDetails()}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm}
            disabled={loading || !canConfirm || statsLoading}>
            {loading ? 'Running...' : `Run ${action.label}`}
          </button>
        </div>
      </div>
    </div>
  );
};
